<template>
  <header
    :class="[
      'document-header-editor',
      `document-header-editor--${orientation}`,
      { 'document-header-editor--english': isEnglish }
    ]"
  >
    <div class="recipient-editor">
      <label class="document-label" :for="`customer-${instanceId}`">{{ label('宛先', 'Bill To') }}</label>
      <div class="recipient-primary">
        <CustomerSelectBox
          normalize-width
          allow-custom
          :model-value="model.m_customer_id"
          :custom-value="model.recipient_customer_nm"
          :max-length="255"
          :options="customers"
          :placeholder="label('顧客名を入力・候補から選択', 'Enter or select a customer')"
          @update:modelValue="selectCustomer"
          @update:customValue="model.recipient_customer_nm = $event"
        />
        <span v-if="!isEnglish" class="honorific">御中</span>
      </div>
      <div class="recipient-secondary">
        <CustomerRepSelectBox
          normalize-width
          :model-value="model.m_customer_representative_id"
          :options="representatives"
          :placeholder="label('顧客担当者を検索・選択', 'Select a contact person')"
          @update:modelValue="model.m_customer_representative_id = $event || ''"
        />
      </div>
      <div class="recipient-address">{{ recipientAddress || label('顧客選択後に宛先住所を表示', 'The billing address appears after selecting a customer.') }}</div>

      <table v-if="!isLandscape" class="portrait-subject-editor">
        <tbody>
          <tr v-if="isTemporary">
            <th>{{ label('物件名', 'Property') }}</th>
            <td>
              <BuildingSelectBox
                normalize-width
                allow-custom
                :model-value="model.m_building_id"
                :custom-value="model.building_nm"
                :max-length="255"
                :options="buildings"
                :placeholder="label('物件名を入力・候補から選択', 'Enter or select a property')"
                @update:modelValue="selectBuilding"
                @update:customValue="model.building_nm = $event"
              />
            </td>
          </tr>
          <tr>
            <th>
              {{ firstSubjectLabel }}
              <span class="badge text-bg-danger">{{ label('必須', 'Required') }}</span>
            </th>
            <td>
              <input
                v-model.trim="model.title"
                class="document-input"
                type="text"
                required
                :placeholder="subjectPlaceholder"
              >
            </td>
          </tr>
          <tr v-if="!isTemporary">
            <th>{{ locationLabel }}</th>
            <td>
              <div class="portrait-location-editor">
                <BuildingSelectBox
                  normalize-width
                  allow-custom
                  :model-value="model.m_building_id"
                  :custom-value="model.building_nm"
                  :max-length="255"
                  :options="buildings"
                  :placeholder="label('建物名を入力・候補から選択', 'Enter or select a building')"
                  @update:modelValue="selectBuilding"
                  @update:customValue="model.building_nm = $event"
                />
                <textarea
                  v-model="model.work_location"
                  class="document-input"
                  rows="1"
                  :placeholder="label('履行場所の住所（建物マスタの住所を使う場合は空欄）', 'Service location (leave blank to use the building address)')"
                ></textarea>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="document-title-block">
      <h2>{{ label('御 見 積 書', 'QUOTATION') }}</h2>
      <span v-if="pageLabel" class="page-label">{{ pageLabel }}</span>
    </div>

    <div class="issuer-editor">
      <div class="document-meta-editor">
        <template v-if="isLandscape">
          <div>
            {{ label('見積番号', 'Quotation No.') }} {{ model.estimate_no || label('保存時に自動採番', 'Assigned after saving') }}
            <span
              v-if="Number(model.revision_no) > 0"
              class="revision-label"
            >{{ formatRevisionLabel(model.revision_no) }}</span>
          </div>
          <label :for="`issue-date-${instanceId}`" class="document-meta-row">
            <span>
              {{ label('発行日', 'Issue Date') }}
              <span class="badge text-bg-danger">{{ label('必須', 'Required') }}</span>
            </span>
            <input
              :id="`issue-date-${instanceId}`"
              v-model="model.issue_date"
              class="document-input document-date-input"
              type="date"
              required
            >
          </label>
        </template>
        <template v-else>
          <label :for="`issue-date-${instanceId}`" class="document-meta-row">
            <span>
              {{ label('発行日', 'Issue Date') }}
              <span class="badge text-bg-danger">{{ label('必須', 'Required') }}</span>
            </span>
            <input
              :id="`issue-date-${instanceId}`"
              v-model="model.issue_date"
              class="document-input document-date-input"
              type="date"
              required
            >
          </label>
          <div class="document-meta-row">
            <span>{{ label('見積番号', 'Quotation No.') }}</span>
            <span>{{ model.estimate_no || label('保存時に自動採番', 'Assigned after saving') }}</span>
          </div>
        </template>
      </div>
      <div
        :class="[
          'company-editor',
          { 'company-editor--with-seal': Boolean(model.seal_flg) }
        ]"
      >
        <strong>{{ label('株式会社 創英企画', 'Soei Planning Co., Ltd.') }}</strong>
        <span>{{ label('〒151-0053 東京都渋谷区代々木2丁目38番6号', '2-38-6 Yoyogi, Shibuya-ku, Tokyo 151-0053, Japan') }}</span>
        <span>{{ label('TEL 03-3370-8006 / FAX 03-3370-8157', 'Tel: 03-3370-8006 / Fax: 03-3370-8157') }}</span>
        <CompanySeal
          :visible="Boolean(model.seal_flg)"
          class="company-editor-seal"
        />
      </div>
      <div class="stamp-editor" :aria-label="label('押印欄', 'Approval stamps')">
        <template v-if="isLandscape">
          <span>{{ label('承認', 'Approved') }}</span><span>{{ label('確認', 'Reviewed') }}</span><span>{{ label('作成', 'Prepared') }}</span>
          <span>
            <EmployeeSeal
              :name="model.approved_stamp_name || model.approved_by_user?.name || ''"
              :image-url="model.approved_stamp_image_url || ''"
              :size="36"
              class="approval-employee-seal"
            />
          </span>
          <span></span>
          <span>
            <EmployeeSeal
              :name="model.created_stamp_name || model.created_by_user?.name || ''"
              :image-url="model.created_stamp_image_url || ''"
              :size="36"
              class="approval-employee-seal"
            />
          </span>
        </template>
        <template v-else>
          <span>{{ label('承認', 'Approved') }}</span><span>{{ label('確認', 'Reviewed') }}</span><span>{{ label('作成', 'Prepared') }}</span>
          <span>
            <EmployeeSeal
              :name="model.approved_stamp_name || model.approved_by_user?.name || ''"
              :image-url="model.approved_stamp_image_url || ''"
              :size="36"
              class="approval-employee-seal"
            />
          </span>
          <span></span>
          <span>
            <EmployeeSeal
              :name="model.created_stamp_name || model.created_by_user?.name || ''"
              :image-url="model.created_stamp_image_url || ''"
              :size="36"
              class="approval-employee-seal"
            />
          </span>
        </template>
      </div>
    </div>
  </header>

  <table v-if="isLandscape" class="subject-editor-table">
    <tbody>
      <tr v-if="isTemporary">
        <th>{{ label('物件名', 'Property') }}</th>
        <td>
          <BuildingSelectBox
            normalize-width
            allow-custom
            :model-value="model.m_building_id"
            :custom-value="model.building_nm"
            :max-length="255"
            :options="buildings"
            :placeholder="label('物件名を入力・候補から選択', 'Enter or select a property')"
            @update:modelValue="selectBuilding"
            @update:customValue="model.building_nm = $event"
          />
        </td>
      </tr>
      <tr>
        <th>
          {{ firstSubjectLabel }}
          <span class="badge text-bg-danger">{{ label('必須', 'Required') }}</span>
        </th>
        <td>
          <input
            v-model.trim="model.title"
            class="document-input"
            type="text"
            required
            :placeholder="subjectPlaceholder"
          >
        </td>
      </tr>
      <tr v-if="!isTemporary">
        <th>{{ locationLabel }}</th>
        <td>
          <div class="location-editor">
            <BuildingSelectBox
              normalize-width
              allow-custom
              :model-value="model.m_building_id"
              :custom-value="model.building_nm"
              :max-length="255"
              :options="buildings"
              :placeholder="label('建物名を入力・候補から選択', 'Enter or select a building')"
              @update:modelValue="selectBuilding"
              @update:customValue="model.building_nm = $event"
            />
            <textarea
              v-model="model.work_location"
              class="document-input"
              rows="1"
              :placeholder="label('工事場所の住所（建物マスタの住所を使う場合は空欄）', 'Work location (leave blank to use the building address)')"
            ></textarea>
          </div>
        </td>
      </tr>
      <tr v-if="isLandscape">
        <th>{{ label('工期', 'Work Period') }}</th>
        <td>
          <textarea v-model="model.delivery_terms" class="document-input" rows="1" :placeholder="label('工期を入力', 'Enter the work period')"></textarea>
        </td>
      </tr>
      <tr v-if="isLandscape">
        <th>{{ label('有効期限', 'Valid Until') }}</th>
        <td><input v-model="model.valid_until" class="document-input document-date-input" type="date"></td>
      </tr>
    </tbody>
  </table>

  <div :class="['amount-editor-row', { 'amount-editor-row--landscape': isLandscape, 'amount-editor-row--portrait': !isLandscape }]">
    <div class="grand-total-editor">
      <span>{{ totalLabel }}</span>
      <strong>{{ isEnglish ? '¥' : '￥' }}{{ formatNumber(displayTotal) }}{{ isEnglish ? '' : '.-' }}</strong>
      <small>{{ totalSuffix }}</small>
    </div>
    <table v-if="!isAnnual" class="tax-summary-editor">
      <tbody>
        <tr><th>{{ isLandscape ? label('工事費', 'Construction Cost') : label('見積金額', 'Net Amount') }}</th><td>{{ formatNumber(preview.net) }}</td></tr>
        <tr><th>{{ label('消費税', 'Tax') }}</th><td>{{ formatNumber(preview.tax) }}</td></tr>
        <tr v-if="isLandscape" class="tax-summary-total"><th>{{ label('合計', 'Total') }}</th><td>{{ formatNumber(preview.total) }}</td></tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { isEnglishEstimateLanguage } from '@/utils/estimateEditor';
import { formatRevisionLabel } from '@/utils/estimates';
import BuildingSelectBox from '@/components/searchSelectBoxes/BuildingSelectBox.vue';
import CustomerRepSelectBox from '@/components/searchSelectBoxes/CustomerRepSelectBox.vue';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';
import CompanySeal from '@/components/shared/CompanySeal.vue';
import EmployeeSeal from '@/components/shared/EmployeeSeal.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  orientation: {
    type: String,
    default: 'portrait',
    validator: value => ['portrait', 'landscape'].includes(value)
  },
  variant: {
    type: String,
    default: 'temporary',
    validator: value => ['annual', 'temporary', 'landscape'].includes(value)
  },
  customers: {
    type: Array,
    default: () => []
  },
  representatives: {
    type: Array,
    default: () => []
  },
  buildings: {
    type: Array,
    default: () => []
  },
  preview: {
    type: Object,
    required: true
  },
  pageLabel: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue', 'customer-changed']);
const model = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
});
const instanceId = Math.random().toString(36).slice(2);
const isEnglish = computed(() => isEnglishEstimateLanguage(model.value.default_language_type));
const label = (japanese, english) => isEnglish.value ? english : japanese;
const isLandscape = computed(() => props.orientation === 'landscape');
const isAnnual = computed(() => props.variant === 'annual');
const isTemporary = computed(() => props.variant === 'temporary');
const selectedCustomer = computed(() =>
  props.customers.find(customer => String(customer.id) === String(model.value.m_customer_id))
);
const recipientAddress = computed(() => {
  const snapshotCustomerId = model.value.customer?.id || model.value.m_customer?.id;
  const snapshotStillSelected =
    snapshotCustomerId && String(snapshotCustomerId) === String(model.value.m_customer_id);
  if (snapshotStillSelected && model.value.recipient?.address) {
    const postalCode = model.value.recipient.postal_code
      ? `〒${model.value.recipient.postal_code}`
      : '';
    return [postalCode, model.value.recipient.address].filter(Boolean).join(' ');
  }
  const customer = selectedCustomer.value;
  if (!customer) return '';
  const address = [customer.prefecture, customer.city, customer.other_address]
    .filter(Boolean)
    .join('');
  return [customer.postal_code ? `〒${customer.postal_code}` : '', address]
    .filter(Boolean)
    .join(' ');
});
const firstSubjectLabel = computed(() => {
  if (props.orientation === 'landscape') return label('工事名', 'Project Name');
  return isAnnual.value ? label('件名', 'Subject') : label('見積件名', 'Quotation Subject');
});
const subjectPlaceholder = computed(() =>
  isEnglish.value ? `Enter ${firstSubjectLabel.value}` : `${firstSubjectLabel.value}を入力`
);
const locationLabel = computed(() =>
  props.orientation === 'landscape'
    ? label('工事場所', 'Work Location')
    : label('履行場所', 'Service Location')
);
const totalLabel = computed(() => {
  if (isLandscape.value) return label('御見積金額', 'Total Quotation Amount');
  return isAnnual.value
    ? label('合 計 金 額', 'Total Amount')
    : label('見積合計金額', 'Total Quotation Amount');
});
// 年間見積は見本どおり税抜の年額を表示する（2026/09/28 お客様確認）
const totalSuffix = computed(() => {
  if (isLandscape.value) return isEnglish.value ? '(Tax included)' : '（消費税込）';
  if (isAnnual.value) return isEnglish.value ? '(Annual)' : '（年額）';
  return isEnglish.value ? '(Tax included)' : '（税込）';
});
const displayTotal = computed(() =>
  isAnnual.value ? props.preview.net : props.preview.total
);
const selectCustomer = value => {
  model.value.m_customer_id = value || '';
  if (value) model.value.recipient_customer_nm = '';
  emit('customer-changed');
};
const selectBuilding = value => {
  model.value.m_building_id = value || '';
  if (value) model.value.building_nm = '';
};
const formatNumber = value =>
  Math.round(Number(value || 0)).toLocaleString(isEnglish.value ? 'en-US' : 'ja-JP');
</script>

<style scoped>
.document-header-editor {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(240px, 0.8fr) minmax(0, 1.15fr);
  min-height: 150px;
  gap: 18px;
}

.document-header-editor--portrait {
  min-height: 244px;
  grid-template-columns: minmax(0, 1.35fr) minmax(220px, 0.7fr) minmax(0, 1fr);
  gap: 22px;
}

.document-header-editor--portrait .recipient-editor,
.document-header-editor--portrait .issuer-editor {
  padding-top: 62px;
}

.document-header-editor--portrait .document-label {
  display: none;
}

.document-header-editor--portrait .document-title-block h2 {
  margin: 24px 0 4px;
  padding: 0;
  border: 0;
  font-size: 26px;
  letter-spacing: 0.34em;
}

.document-header-editor--landscape {
  min-height: 210px;
  grid-template-columns: minmax(0, 1.45fr) minmax(340px, 0.75fr);
  grid-template-rows: auto 1fr;
  column-gap: 80px;
}

.document-header-editor--landscape .document-title-block {
  grid-row: 1;
  grid-column: 1 / -1;
}

.document-header-editor--landscape .document-title-block h2 {
  margin: 0 0 22px;
  padding: 0;
  border: 0;
  font-size: 30px;
  font-weight: 600;
  letter-spacing: 0.5em;
}

.document-header-editor--landscape .recipient-editor {
  grid-row: 2;
  grid-column: 1;
}

.document-header-editor--landscape .issuer-editor {
  grid-row: 2;
  grid-column: 2;
}

.recipient-editor,
.issuer-editor {
  font-size: 12px;
}

.document-label {
  display: block;
  margin-bottom: 3px;
  color: #51616f;
  font-size: 10px;
  font-weight: 700;
}

.recipient-primary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
}

.recipient-secondary {
  margin-top: 6px;
}

.honorific {
  min-width: 2em;
  font-family: serif;
  font-size: 14px;
}

.recipient-address {
  min-height: 20px;
  margin-top: 8px;
  color: #46545f;
  white-space: pre-line;
}

.portrait-subject-editor {
  width: 100%;
  margin-top: 7px;
  border-collapse: separate;
  border-spacing: 0 5px;
}

.portrait-subject-editor th {
  width: 64px;
  padding: 0 7px 0 0;
  color: #536473;
  font-size: 10.5px;
  font-weight: 500;
  text-align: left;
  white-space: nowrap;
}

.portrait-subject-editor td {
  padding: 0;
}

.portrait-location-editor {
  display: grid;
  gap: 4px;
}

.document-title-block {
  align-self: start;
  text-align: center;
}

.document-title-block h2 {
  margin: 28px 0 4px;
  padding-bottom: 8px;
  border-bottom: 2px solid #222;
  font-family: "Yu Mincho", "Hiragino Mincho ProN", serif;
  font-size: 27px;
  font-weight: 500;
  letter-spacing: 0.24em;
  white-space: nowrap;
}

.page-label {
  color: #64727e;
  font-size: 11px;
}

.issuer-editor {
  position: relative;
  padding-bottom: 68px;
  text-align: right;
}

.document-meta-editor {
  display: grid;
  justify-items: end;
  gap: 4px;
}

.document-meta-row {
  display: grid;
  width: 100%;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
}

.document-meta-row > span:first-child {
  color: #536473;
  font-size: 10.5px;
  white-space: nowrap;
}

.revision-label {
  margin-left: 1em;
}

.document-date-input {
  max-width: 170px;
}

.company-editor {
  position: relative;
  display: flex;
  box-sizing: border-box;
  min-height: 60px;
  margin-top: 16px;
  flex-direction: column;
  line-height: 1.6;
}

.company-editor--with-seal {
  padding-top: 66px;
}

.company-editor-seal {
  position: absolute;
  top: 0;
  right: 60px;
}

.company-editor strong {
  font-family: serif;
  font-size: 14px;
}

.stamp-editor {
  position: absolute;
  right: 0;
  bottom: 0;
  display: grid;
  width: 132px;
  grid-template-columns: repeat(3, 44px);
  grid-template-rows: 18px 44px;
  border-top: 1px solid #333;
  border-left: 1px solid #333;
  font-size: 8px;
}

.stamp-editor span {
  display: flex;
  box-sizing: border-box;
  min-width: 0;
  min-height: 0;
  padding: 2px;
  border-right: 1px solid #333;
  border-bottom: 1px solid #333;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.stamp-editor > span:nth-child(n + 4) {
  position: relative;
}

.stamp-editor .approval-employee-seal {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 36px;
  height: 36px;
  margin: auto;
}

.subject-editor-table,
.tax-summary-editor {
  width: 100%;
  border-collapse: collapse;
}

.subject-editor-table {
  margin: 10px 0 16px;
}

.subject-editor-table th,
.subject-editor-table td {
  border: 1px solid #333;
}

.subject-editor-table th {
  width: 135px;
  padding: 8px 12px;
  background: #f3f1ea;
  font-family: serif;
  font-weight: 500;
  text-align: center;
}

.subject-editor-table td {
  padding: 4px 6px;
}

.location-editor {
  display: grid;
  grid-template-columns: minmax(280px, 0.9fr) minmax(260px, 1.1fr);
  align-items: start;
  gap: 8px;
}

.document-input {
  width: 100%;
  min-height: 32px;
  padding: 5px 7px;
  border: 1px solid #ced4da;
  border-radius: 2px;
  background: #fff;
  font: inherit;
}

.document-input:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.18);
}

/* 縦型の入力欄は請求書作成画面などと同じ標準フォームの見た目に揃える */
.document-header-editor--portrait .recipient-editor,
.document-header-editor--portrait .issuer-editor {
  font-size: 14px;
}

.document-header-editor--portrait .portrait-subject-editor th,
.document-header-editor--portrait .document-meta-row > span:first-child {
  width: 84px;
  font-size: 13px;
}

.document-header-editor--portrait .document-input,
.document-header-editor--portrait :deep(.form-control) {
  min-height: 38px;
  padding: 6px 12px;
  border-color: #ced4da;
  border-radius: 0.375rem;
  background: #fff;
  font-family: var(--bs-body-font-family);
  font-size: 14px;
}

.document-header-editor--portrait .language-fallback-input {
  font-size: 12px;
}

.document-header-editor--portrait .document-date-input {
  max-width: 190px;
}

/* 英語は「Issue Date」と「Required」が長いため、ラベル幅を確保して日付欄との重なりを防ぐ */
.document-header-editor--portrait.document-header-editor--english .document-meta-row {
  grid-template-columns: minmax(112px, max-content) minmax(0, 1fr);
  gap: 10px;
}

.document-header-editor--portrait.document-header-editor--english .document-meta-row > span:first-child {
  display: inline-flex;
  width: auto;
  min-width: 112px;
  align-items: center;
  gap: 4px;
}

.language-fallback-input {
  margin-top: 4px;
  border-color: #ced4da;
  background: #fff;
  font-family: system-ui, sans-serif;
  font-size: 11px;
}

.amount-editor-row {
  display: flex;
  min-height: 62px;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
  gap: 38px;
}

.grand-total-editor {
  display: flex;
  min-width: 430px;
  padding: 8px 14px;
  border-bottom: 2px solid #222;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  font-family: serif;
  font-size: 19px;
}

.grand-total-editor strong {
  font-size: 24px;
  font-weight: 500;
}

.grand-total-editor small {
  font-size: 12px;
}

.tax-summary-editor {
  width: 250px;
  font-size: 12px;
}

.tax-summary-editor th,
.tax-summary-editor td {
  padding: 5px 8px;
  border-bottom: 1px solid #555;
}

.tax-summary-editor td {
  text-align: right;
}

.amount-editor-row--portrait {
  min-height: 60px;
  margin: 16px 0 18px;
  border: 2px solid #2f3337;
  justify-content: space-between;
  gap: 0;
}

.amount-editor-row--portrait .grand-total-editor {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border-bottom: 0;
}

.amount-editor-row--portrait .tax-summary-editor {
  align-self: stretch;
  width: 330px;
  border-left: 1px solid #2f3337;
}

.amount-editor-row--landscape {
  display: grid;
  margin-top: 20px;
  grid-template-columns: minmax(0, 1fr) 360px;
  align-items: start;
  gap: 24px;
}

.amount-editor-row--landscape .grand-total-editor {
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  padding: 12px 18px;
  border: 2px solid #222;
}

.amount-editor-row--landscape .tax-summary-editor {
  width: 100%;
}

.tax-summary-total th,
.tax-summary-total td {
  border-top: 2px solid #333;
  font-weight: 700;
}

:deep(.relative) {
  min-width: 0;
}

:deep(.form-control) {
  font-size: 12px;
}

@media (max-width: 1100px) {
  .document-header-editor {
    grid-template-columns: 1fr;
  }

  .document-title-block {
    grid-row: 1;
  }

  .issuer-editor {
    min-height: 120px;
  }

  .location-editor {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .amount-editor-row {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .grand-total-editor,
  .tax-summary-editor {
    width: 100%;
    min-width: 0;
  }
}
</style>
